import { CloseOutlineIcon } from '@carboncopyco/icons';
import { ColorHex, TextBoxEditorTitle } from '@carboncopyco/ui';
import { getMaxZIndex } from '@carboncopyco/utils';
import classNames from 'classnames';
import type React from 'react';
import { forwardRef } from 'react';

export interface EditToolDialogProps {
  children: string | React.ReactNode;
  title?: string | React.ReactNode;
  onClose?: () => void;
  dragHandler?: string;
  draggable?: boolean;
  headless?: boolean;
  withOverflowHidden?: boolean;
  removeXIcon?: boolean;
}

const EditToolDialogTitle = ({ title }: { title: string }) => {
  return (
    <TextBoxEditorTitle
      label={title}
      uppercase
      fontWeight="font-ds-medium"
      color="text-ds-natural-600"
    />
  );
};

const EditToolDialog = forwardRef<HTMLDivElement, EditToolDialogProps>(
  (
    {
      children,
      title = '',
      onClose,
      dragHandler = 'drag-handler',
      draggable = true,
      headless = false,
      withOverflowHidden = true,
      removeXIcon = false,
    },
    ref
  ) => {
    return (
      <div
        style={{
          zIndex: getMaxZIndex() + 1,
        }}
        ref={ref}
        className={classNames(
          'flex flex-col bg-ds-accent3-100 box-border rounded-md shadow-ds-edit-tool inline-block relative',
          {
            'overflow-hidden': withOverflowHidden,
          }
        )}
        data-edit-tool="true"
      >
        <div
          className={classNames(
            'flex items-center justify-between box-border my-px',
            {
              [dragHandler]: draggable,
              'cursor-move': draggable,
              'h-ds-12 p-4': !headless,
              'h-4': headless,
            }
          )}
          data-edit-tool="true"
        >
          {!headless && (
            <>
              {typeof title === 'string' ? (
                <EditToolDialogTitle title={title} />
              ) : (
                title
              )}
              {!removeXIcon && (
                <div
                  className="cursor-pointer h-full flex items-center"
                  onClick={onClose}
                >
                  <CloseOutlineIcon
                    color={ColorHex.natural600}
                    size={20}
                  />
                </div>
              )}
            </>
          )}
        </div>
        {children}
      </div>
    );
  }
);

EditToolDialog.displayName = 'EditToolDialog';

const EditToolDialogNamespace = Object.assign(EditToolDialog, {
  Title: EditToolDialogTitle,
});

export { EditToolDialogNamespace as EditToolDialog };
